import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';
import { StatusBadge, type StatusBadgeStatus } from './status-badge';

/**
 * Story 2.4 (FR13/FR14, AD-18) — het event-dekkingsoverzicht: per proces/stap gedekt (`check`
 * groen + volume/datum) of niet-gedekt (`b-nodata`/`b-info`), plus een samenvattende "N van 10"
 * segment-balk. Reuses the Story 2.2 coverage-* StatusBadge variants + `process-complete` for
 * "gedekt" (DESIGN.md's dedicated CoverageMeter recipe: groene `check`, anders dan de status-
 * strip's teal `adjust`-behandeling van datzelfde coverageState).
 */
export type CoverageMeterCoverageState = 'covered' | 'no_data' | 'publisher_offline' | 'unmapped';

export interface CoverageMeterStepItem {
  canonicalStep: string;
  /** Localized step label (this component is taal-neutraal — the caller translates). */
  stepLabel: string;
  coverageState: CoverageMeterCoverageState;
  count: number | null;
  /** Pre-formatted "datum laatste event" (locale-aware); undefined when unavailable (Story 2.4 scope gap — see StepCoverageEntry doc). */
  lastEventLabel?: string;
  /** Localized StatusBadge label (e.g. "Gedekt", "Publisher niet live"). */
  statusLabel: string;
}

export interface CoverageMeterProcessGroup {
  name: string;
  /** Localized process label. */
  processLabel: string;
  steps: CoverageMeterStepItem[];
}

export interface CoverageMeterProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  groups: CoverageMeterProcessGroup[];
  /** Denominator-let-op (Story 2.4 AC1): dit slaat op het fysieke doelbeeld (10), NIET op de 22 canonieke stappen over alle processen. */
  physicalTargetSummary: { covered: number; denominator: number };
  /** Pre-composed "N van 10 stappen data-gedekt"-tekst (locale-aware; de caller vertaalt). */
  summaryLabel: string;
}

const STEP_STATUS_TO_BADGE: Record<CoverageMeterCoverageState, StatusBadgeStatus> = {
  covered: 'process-complete',
  no_data: 'coverage-no-data',
  publisher_offline: 'coverage-publisher-offline',
  unmapped: 'coverage-unmapped',
};

// Same static-literal-class-bucket pattern as FunnelBar (Tailwind's scanner requires literal
// class text, not a runtime-interpolated `w-[${n}%]`).
const SEGMENT_WIDTH_CLASSES: Record<number, string> = {
  0: 'w-0', 5: 'w-[5%]', 10: 'w-[10%]', 15: 'w-[15%]', 20: 'w-[20%]', 25: 'w-[25%]',
  30: 'w-[30%]', 35: 'w-[35%]', 40: 'w-[40%]', 45: 'w-[45%]', 50: 'w-[50%]', 55: 'w-[55%]',
  60: 'w-[60%]', 65: 'w-[65%]', 70: 'w-[70%]', 75: 'w-[75%]', 80: 'w-[80%]', 85: 'w-[85%]',
  90: 'w-[90%]', 95: 'w-[95%]', 100: 'w-full',
};

function segmentWidthClass(covered: number, denominator: number): string {
  if (denominator <= 0 || covered <= 0) return SEGMENT_WIDTH_CLASSES[0]!;
  const pct = Math.min(100, Math.max(0, (covered / denominator) * 100));
  const bucket = (Math.round(pct / 5) * 5) as keyof typeof SEGMENT_WIDTH_CLASSES;
  return SEGMENT_WIDTH_CLASSES[bucket] ?? SEGMENT_WIDTH_CLASSES[100]!;
}

export function CoverageMeter({ className, groups, physicalTargetSummary, summaryLabel, ...props }: CoverageMeterProps) {
  return (
    <div {...props} className={cn('flex flex-col gap-4', className)} data-testid="coverage-meter">
      <div data-testid="coverage-meter-summary">
        <p className="text-sm font-semibold text-[var(--foreground)]">{summaryLabel}</p>
        <div aria-label={summaryLabel} className="mt-1 h-2 w-full overflow-hidden rounded-[var(--radius-control)] bg-[var(--background-muted)]" role="img">
          <div className={cn('h-2 rounded-[var(--radius-control)] bg-[var(--success)]', segmentWidthClass(physicalTargetSummary.covered, physicalTargetSummary.denominator))} />
        </div>
      </div>

      {groups.map((group) => (
        <div data-testid={`coverage-meter-group-${group.name}`} key={group.name}>
          <h4 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--foreground-muted)]">{group.processLabel}</h4>
          <ul className="mt-2 flex flex-col gap-2" role="list">
            {group.steps.map((step) => (
              <li
                className="flex items-center justify-between gap-2 text-xs"
                data-coverage-state={step.coverageState}
                data-testid="coverage-meter-step"
                key={step.canonicalStep}
              >
                <span className="text-[var(--foreground-muted)]">{step.stepLabel}</span>
                <div className="flex items-center gap-2">
                  {step.count != null ? (
                    <span className="text-[var(--foreground)]" data-testid="coverage-meter-step-count">
                      {step.count}
                      {step.lastEventLabel ? ` · ${step.lastEventLabel}` : ''}
                    </span>
                  ) : null}
                  <StatusBadge label={step.statusLabel} status={STEP_STATUS_TO_BADGE[step.coverageState]} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
